import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

import { GoogleMapsLoader } from '@/design-system/helpers/GoogleMapsLoader';
import { trackInteraction } from '@/utils/analytics';
import { arrayNotEmpty } from '@/utils/arrayNotEmpty';
import { fetchLocationWaitTime } from '@/utils/fetchLocationWaitTime';
import { normalizeAddress } from '@/utils/normalizeAddress';
import { normalizeWorkingHours } from '@/utils/normalizeWorkingHours';
import window from '@/utils/window';

import { addClusterMarkers } from './components/addClusterMarkers';
import InfoWindow from './components/InfoWindow/InfoWindow';
import { MapContextProvider, useMapContext } from './contexts/MapContext';
import styles from './Map.module.scss';

const LocationDetailsCards = (props) => {
    const { items } = props;

    const mapContext = useMapContext();

    const currentKey = mapContext.currentKey;
    const currentItem = items.find((item) => item.key === currentKey);

    React.useLayoutEffect(() => {
        document
            .querySelectorAll('[data-map-marker-key]')
            ?.forEach((e) => e.classList.remove('active'));
        document
            .querySelector(`[data-map-marker-key="${mapContext.currentKey}"]`)
            ?.classList.add('active');
    }, [mapContext.currentKey]);

    if (!currentItem?.content) {
        return null;
    }

    return (
        <div
            style={{
                position: 'absolute',
                bottom: 16,
                left: 16,
                width: 'calc(100% - 32px)',
            }}
        >
            {currentItem.content}
        </div>
    );
};

const Marker = (props) => {
    return null;
};

const GoogleMaps = ({ children, center: centerProp, zoom, mapId, analytics }) => {
    const ref = useRef();
    const mapElementId = useId();
    const [googleMaps, setGoogleMaps] = useState(null);
    const [waitTimeArr, setWaitTimeArr] = useState([]);
    const removeMarkerClusters = useRef(null);

    const mapContext = useMapContext();

    useEffect(() => {
        Promise.all(
            React.Children.map(children, (child) =>
                fetchLocationWaitTime(
                    child.props?.locationType,
                    child.props?.waitTime,
                    child.props?.epicDepartmentId,
                ),
            ),
        ).then((data) => {
            setWaitTimeArr(data);
        });
    }, [children]);

    const items = React.useMemo(() => {
        const uid = () => {
            return Date.now().toString(36) + Math.random().toString(36).substr(2);
        };
        return React.Children.map(children, (child, index) => {
            if (!child) {
                return null;
            }
            // analytics
            const { is24Hours, isOpen } = normalizeWorkingHours({
                workingHours: child.props?.workingHours,
            });
            const analyticsObj = {
                interactionType: 'location_map_click',
                actionLabel: child.props?.title,
                locationName: child.props?.title,
                locationParent: child.props?.parentLocationName,
                locationAddress: normalizeAddress(child.props?.address).replaceAll('\n', ','),
                locationPhone: child.props?.phoneNumber,
                locationOpen: is24Hours ? 'Open 24 Hours' : isOpen ? 'Open' : 'Closed',
                locationWait: waitTimeArr[index] || '',
                selector: 'location',
            };

            return {
                key: child.props.id ?? uid(),
                ...child.props,
                content: child.props.children,
                itemAnalytics: analyticsObj,
            };
        });
    }, [children, waitTimeArr]);

    useEffect(() => {
        mapContext.setItems(items);
    }, [mapContext, items]);

    const firstRenderRef = useRef(true);

    /**
     * Get bounds and center of items/markers
     */
    const [bounds, center] = React.useMemo(() => {
        const bounds = new window.google.maps.LatLngBounds();
        if (items && items.length > 0) {
            const markers = Array.isArray(items) ? items : [items];
            markers.forEach((item) => {
                const latLng = new window.google.maps.LatLng(
                    item?.position.lat,
                    item?.position.lng,
                );
                bounds.extend(latLng);
            });
        }
        const center =
            centerProp?.lat && centerProp?.lng
                ? { lat: Number(centerProp.lat), lng: Number(centerProp.lng) }
                : bounds.getCenter();

        return [bounds, center];
    }, [items, centerProp?.lat, centerProp?.lng]);

    /**
     * Update the bounds of the map to fit all active markers
     * @returns {void}
     */
    const updateBounds = useCallback(() => {
        if (!googleMaps) {
            return;
        }

        googleMaps.fitBounds(bounds);
    }, [googleMaps, bounds]);

    /**
     * Display the map
     */
    useEffect(() => {
        // Display the map
        if (!ref.current || googleMaps) {
            return;
        }

        const newMap = new window.google.maps.Map(ref.current, {
            mapId,
            ...(center && { center }),
            ...(zoom && { zoom }),
            gestureHandling: 'cooperative',
        });

        setGoogleMaps(newMap);

        firstRenderRef.current = false;
    }, [ref, center, googleMaps, zoom, mapId]);

    /**
     * Set zoom level of the map
     */
    useEffect(() => {
        if (!googleMaps || !zoom || firstRenderRef.current) {
            return;
        }

        googleMaps.setZoom(zoom);
    }, [googleMaps, zoom]);

    /**
     * Set center of the map
     */
    useEffect(() => {
        if (!googleMaps || !center || firstRenderRef.current) {
            return;
        }

        googleMaps.setCenter(center);
    }, [googleMaps, center]);

    /**
     * Add markers to the map
     */
    useEffect(() => {
        // clear any existing markers before adding new ones
        if (typeof removeMarkerClusters.current === 'function') {
            removeMarkerClusters.current();
        }

        addClusterMarkers({
            map: googleMaps,
            items: items,
            setCurrentKey: mapContext.setCurrentKey,
            analytics: analytics,
        }).then((removeMarkers) => {
            removeMarkerClusters.current = removeMarkers;
            updateBounds();
        });
    }, [
        googleMaps,
        items,
        mapContext.setCurrentKey,
        removeMarkerClusters,
        analytics,
        updateBounds,
    ]);

    /**
     * Hide info window when clicked outside
     */
    useEffect(() => {
        if (!googleMaps) {
            return;
        }

        window.google.maps.event.addListener(googleMaps, 'click', () => {
            mapContext.setCurrentKey(null);
        });
    }, [googleMaps, mapContext]);

    /**
     * Fit the map to the bounds of the markers
     */
    useEffect(() => {
        if (!googleMaps || center) {
            return;
        }

        window.google.maps.event.addListenerOnce(googleMaps, 'idle', function () {
            updateBounds();
        });
    }, [googleMaps, center, updateBounds]);

    /**
     * Add zoom in/out click event analytics
     */
    useEffect(() => {
        if (!googleMaps) {
            return;
        }
        const handleClick = (event) => {
            const ariaLabel = event?.target?.getAttribute('aria-label');
            if (
                analytics &&
                event.target.tagName === 'BUTTON' &&
                ariaLabel &&
                (ariaLabel === 'Zoom in' || ariaLabel === 'Zoom out')
            ) {
                trackInteraction({
                    ...analytics,
                    actionLabel: event.target.getAttribute('aria-label'),
                    interactionType: 'location_map_zoom',
                    selector: 'button',
                });
            }
        };
        const mapDiv = document.getElementById(mapElementId);
        if (mapDiv) {
            mapDiv.addEventListener('click', handleClick);
        }

        return () => {
            if (mapDiv) {
                mapDiv.removeEventListener('click', handleClick);
            }
        };
    }, [googleMaps, analytics, mapElementId]);

    return (
        <div style={{ position: 'relative' }}>
            <div ref={ref} id={mapElementId} className={styles.map} />
            <LocationDetailsCards items={items} />
        </div>
    );
};

const Map = ({
    items,
    apiKey: apiKeyProp,
    mapId: mapIdProp,
    render,
    analytics,
    children,
    ...props
}) => {
    const apiKey = apiKeyProp || 'AIzaSyDRdNprx4liInUfIU8gA6HpPL9p4tu8NYQ';
    const mapId = mapIdProp || 'b936a6d7221f66ee';

    return (
        <MapContextProvider>
            <GoogleMapsLoader apiKey={apiKey} render={render}>
                <GoogleMaps {...props} mapId={mapId} analytics={analytics}>
                    {arrayNotEmpty(items) &&
                        items.map((item, index) => (
                            <Marker key={index} {...item}>
                                <InfoWindow content={item.content} analytics={analytics} />
                            </Marker>
                        ))}
                </GoogleMaps>
            </GoogleMapsLoader>
        </MapContextProvider>
    );
};

Map.defaultProps = {};
export default Map;
